import { posts, uploadFile, mediaUrl } from '../api.js';
import { compressImage } from '../utils/compressImage.js';
import { toast } from '../utils/dom.js';

const MAX_IMAGE = 2 * 1024 * 1024;

/**
 * Open compose modal. opts: { editPost?: {id, content, image_url}, onDone }
 */
export function openCompose(opts = {}) {
  const { editPost = null, onDone } = opts;
  document.getElementById('compose-modal')?.remove();

  const existingImage = editPost?.image_url || editPost?.imageUrl || editPost?.image || '';
  const existingImageAbs = existingImage ? mediaUrl(existingImage) : '';

  const backdrop = document.createElement('div');
  backdrop.id = 'compose-modal';
  backdrop.className = 'modal-backdrop';
  backdrop.innerHTML = `
    <div class="modal" role="dialog" aria-modal="true" aria-labelledby="compose-title">
      <div class="modal-header">
        <h3 id="compose-title">${editPost ? '编辑帖子' : '撰写帖子'}</h3>
        <button type="button" class="btn btn-ghost btn-sm" data-close>✕</button>
      </div>
      <div class="modal-body">
        <textarea id="compose-text" placeholder="分享你的想法…" maxlength="4000">${
          editPost?.content ? escapeText(editPost.content) : ''
        }</textarea>
        <div class="compose-preview ${existingImageAbs ? '' : 'hidden'}" id="compose-preview">
          <img id="compose-preview-img" alt="预览" src="${existingImageAbs}" />
          <button type="button" class="remove-img" id="compose-remove-img" title="移除图片">✕</button>
        </div>
      </div>
      <div class="modal-footer">
        <div class="flex gap-1 items-center">
          <label class="btn btn-ghost btn-sm" title="上传图片（最大 2MB，自动压缩）">
            🖼 图片
            <input type="file" id="compose-file" accept="image/*" class="sr-only" />
          </label>
          <span class="text-xs faint" id="compose-hint">图片最大 2MB · 自动压缩</span>
        </div>
        <button type="button" class="btn btn-primary btn-sm" id="compose-submit">
          ${editPost ? '保存' : '发布'}
        </button>
      </div>
    </div>
  `;

  document.body.appendChild(backdrop);

  let imageFile = null;
  let imageUrl = existingImage || '';
  let imageRemoved = false;

  const close = () => backdrop.remove();

  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) close();
  });
  backdrop.querySelector('[data-close]')?.addEventListener('click', close);

  const preview = backdrop.querySelector('#compose-preview');
  const previewImg = backdrop.querySelector('#compose-preview-img');

  backdrop.querySelector('#compose-remove-img')?.addEventListener('click', () => {
    imageFile = null;
    imageUrl = '';
    imageRemoved = true;
    preview.classList.add('hidden');
    previewImg.src = '';
    backdrop.querySelector('#compose-file').value = '';
  });

  backdrop.querySelector('#compose-file')?.addEventListener('change', async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast('仅支持图片', 'error');
      return;
    }
    const hint = backdrop.querySelector('#compose-hint');
    hint.textContent = '压缩中…';
    try {
      const compressed = await compressImage(file, { maxBytes: MAX_IMAGE });
      imageFile = compressed;
      imageRemoved = false;
      const url = URL.createObjectURL(compressed);
      previewImg.src = url;
      preview.classList.remove('hidden');
      hint.textContent = `已压缩 · ${Math.round(compressed.size / 1024)} KB`;
    } catch (err) {
      toast(err.message || '图片处理失败', 'error');
      e.target.value = '';
      hint.textContent = '图片最大 2MB · 自动压缩';
    }
  });

  backdrop.querySelector('#compose-submit')?.addEventListener('click', async () => {
    const content = backdrop.querySelector('#compose-text').value.trim();
    if (!content && !imageFile && !imageUrl) {
      toast('请输入内容或添加图片', 'error');
      return;
    }
    const btn = backdrop.querySelector('#compose-submit');
    btn.disabled = true;
    btn.textContent = '提交中…';
    try {
      let finalImageUrl = imageUrl;
      if (imageFile) {
        const up = await uploadFile(imageFile, { type: 'image' });
        finalImageUrl = up.url || up.image_url;
        if (!finalImageUrl) throw new Error('上传失败：无返回地址');
      } else if (imageRemoved) {
        finalImageUrl = null;
      }

      if (editPost) {
        await posts.update(editPost.id, {
          content,
          image_url: finalImageUrl,
        });
        toast('已保存', 'success');
      } else {
        const created = await posts.create({
          content,
          image_url: finalImageUrl || null,
        });
        toast('发布成功', 'success');
        if (created?.risk?.message) {
          toast(created.risk.message, 'error');
        }
      }
      close();
      onDone?.();
    } catch (err) {
      toast(err.message || '发布失败', 'error');
    } finally {
      btn.disabled = false;
      btn.textContent = editPost ? '保存' : '发布';
    }
  });

  backdrop.querySelector('#compose-text')?.focus();
  return close;
}

function escapeText(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
